import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPanel.css';
import { API_URL } from '../../config';

function AdminPanel({ user }) {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    role: 'admin',
    gender: ''
  });
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState({
    user_id: '',
    action: '',
    entity_type: '',
    date_from: '',
    date_to: '',
    limit: 100,
    page: 1,
    hideSystemLogs: true
  });
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1
  });

  // Telegram yönetimi state'leri
  const [selectedUserForTelegram, setSelectedUserForTelegram] = useState(null);
  const [telegramInfo, setTelegramInfo] = useState(null);
  const [telegramCode, setTelegramCode] = useState(null);
  const [showTelegramModal, setShowTelegramModal] = useState(false);

  const SYSTEM_ACTIONS = [
    'SERVER_STARTED',
    'SERVER_SHUTDOWN',
    'MTPROTO_INITIALIZED',
    'DATABASE_FIXED',
    'LOGS_CLEANED',
    'SCHEDULER_RUN',
    'CACHE_CLEARED',
    'BACKUP_COMPLETED',
    'SYSTEM_CHECK',
    'HEALTH_CHECK'
  ];

  const getAuthToken = () => localStorage.getItem('token');

  const getAxiosConfig = () => ({
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });

  useEffect(() => {
    if (user.role === 'root') {
      fetchUsers();
      fetchActivityLogs();
      fetchLoginLogs();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/users`, getAxiosConfig());
      setUsers(response.data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      setMessage('Kullanıcılar yüklenemedi: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams(filter).toString();
      const response = await axios.get(`${API_URL}/logs/activity?${params}`, getAxiosConfig());

      let logs = response.data.logs || [];

      if (filter.hideSystemLogs) {
        logs = logs.filter(log => {
          const isSystemUser = log.username === 'Sistem' ||
            log.username === 'System' ||
            log.username === null ||
            log.username === '';
          const isSystemAction = SYSTEM_ACTIONS.includes(log.action);
          return !(isSystemUser && isSystemAction);
        });
      }

      setActivityLogs(logs);

      const filteredTotal = logs.length;
      setPagination({
        ...response.data.pagination,
        total: filter.hideSystemLogs ? filteredTotal : response.data.pagination.total
      });

    } catch (error) {
      console.error('Aktivite logları yüklenemedi:', error);
      setMessage('Aktivite logları yüklenemedi: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/logs/login?limit=100`, getAxiosConfig());
      const logs = response.data.logs || response.data || [];
      setLoginLogs(logs);
    } catch (error) {
      console.error('Login logları yüklenemedi:', error);
      setMessage('Login logları yüklenemedi: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/users`, newUser, getAxiosConfig());
      setMessage('Kullanıcı başarıyla oluşturuldu');
      setNewUser({ username: '', password: '', email: '', role: 'admin' });
      fetchUsers();
      fetchActivityLogs();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Kullanıcı oluşturulamadı');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API_URL}/users/${userId}`, updates, getAxiosConfig());
      setMessage('Kullanıcı güncellendi');
      fetchUsers();
      fetchActivityLogs();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Güncelleme başarısız');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) return;

    try {
      await axios.delete(`${API_URL}/users/${userId}`, getAxiosConfig());
      setMessage('Kullanıcı silindi');
      fetchUsers();
      fetchActivityLogs();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Silme başarısız');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    await handleUpdateUser(userId, { is_active: !currentStatus });
  };

  const cleanOldLogs = async (days) => {
    if (!window.confirm(`${days} günden eski tüm logları silmek istediğinize emin misiniz?`)) return;

    try {
      const response = await axios.delete(`${API_URL}/logs/clean?days=${days}`, getAxiosConfig());
      setMessage(`${response.data.deleted.total} log temizlendi`);
      fetchActivityLogs();
      fetchLoginLogs();
    } catch (error) {
      setMessage('Log temizleme başarısız');
    }
  };

  const showLogDetail = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const refreshAllData = async () => {
    setMessage('Veriler yenileniyor...');
    await Promise.all([
      fetchUsers(),
      fetchActivityLogs(),
      fetchLoginLogs()
    ]);
    setMessage('Tüm veriler güncellendi!');
    setTimeout(() => setMessage(''), 3000);
  };

  // Telegram yönetimi fonksiyonları
  const openTelegramModal = async (userItem) => {
    setSelectedUserForTelegram(userItem);
    setTelegramCode(null);
    setShowTelegramModal(true);

    try {
      const response = await axios.get(`${API_URL}/users/${userItem.id}/telegram`, getAxiosConfig());
      setTelegramInfo(response.data.telegram);
    } catch (error) {
      console.error('Telegram bilgisi alınamadı:', error);
      setTelegramInfo(null);
    }
  };

  const removeTelegramConnection = async () => {
    if (!selectedUserForTelegram) return;
    if (!window.confirm('Bu kullanıcının Telegram bağlantısını kaldırmak istediğinize emin misiniz?')) return;

    try {
      await axios.delete(`${API_URL}/users/${selectedUserForTelegram.id}/telegram`, getAxiosConfig());
      setMessage('Telegram bağlantısı kaldırıldı');
      setTelegramInfo({ connected: false });
      fetchUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Bağlantı kaldırılamadı');
    }
  };

  const generateTelegramCode = async () => {
    if (!selectedUserForTelegram) return;

    try {
      const response = await axios.post(
        `${API_URL}/users/${selectedUserForTelegram.id}/telegram/generate-code`,
        {},
        getAxiosConfig()
      );
      setTelegramCode(response.data);
      setMessage('Bağlantı kodu oluşturuldu');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Kod oluşturulamadı');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    const icons = {
      'CREATE': '+',
      'UPDATE': '~',
      'DELETE': 'x',
      'LOGIN': '>',
      'LOGOUT': '<',
      'VIEW': 'o',
      'PUBLISH': '^',
      'SCHEDULE': '*',
      'ENABLE': '+',
      'DISABLE': '-',
      'USER_CREATED': '+',
      'USER_UPDATED': '~',
      'USER_DELETED': 'x',
      'CHANNEL_CREATED': '+',
      'CHANNEL_UPDATED': '~',
      'CHANNEL_DELETED': 'x',
      'CONTENT_UPDATED': '~',
      'AUTOMATION_RULE_UPDATED': '~',
      'POST_CREATED': '+',
      'POST_UPDATED': '~',
      'POST_DELETED': 'x',
      'TELEGRAM_CONNECTION_REMOVED': 'x',
      'TELEGRAM_LINK_CODE_GENERATED': '+'
    };
    return icons[action] || '*';
  };

  const getEntityTypeLabel = (type) => {
    const labels = {
      'CHANNEL': 'Kanal',
      'CONTENT': 'İçerik',
      'POST': 'Gönderi',
      'AUTOMATION': 'Otomasyon',
      'USER': 'Kullanıcı',
      'TAG': 'Etiket',
      'SYSTEM': 'Sistem'
    };
    return labels[type] || type;
  };

  if (user.role !== 'root') {
    return (
      <div className="admin-panel">
        <div className="access-denied">
          <h2>Erişim Reddedildi</h2>
          <p>Bu sayfayı görüntülemek için root yetkisine sahip olmanız gerekiyor.</p>
          <p>Mevcut rolünüz: <strong>{user.role}</strong></p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <div className="automation-header">
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={refreshAllData}
            className="btn-primary"
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 'bold',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Yükleniyor...' : 'Verileri Yenile'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`admin-message ${message.includes('başarı') || message.includes('güncellendi') || message.includes('silindi') || message.includes('oluşturuldu') || message.includes('kaldırıldı') || message.includes('temizlendi') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="admin-tabs">
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          Kullanıcı Yönetimi ({users.length})
        </button>
        <button
          className={activeTab === 'telegram' ? 'active' : ''}
          onClick={() => setActiveTab('telegram')}
        >
          Telegram Bağlantıları
        </button>
        <button
          className={activeTab === 'activity' ? 'active' : ''}
          onClick={() => setActiveTab('activity')}
        >
          Sistem Logları ({pagination.total || activityLogs.length})
        </button>
        <button
          className={activeTab === 'login' ? 'active' : ''}
          onClick={() => setActiveTab('login')}
        >
          Giriş Logları ({loginLogs.length})
        </button>
        <button
          className={activeTab === 'maintenance' ? 'active' : ''}
          onClick={() => setActiveTab('maintenance')}
        >
          Bakım
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="users-management">
          <div className="create-user-form">
            <h3>Yeni Kullanıcı Ekle</h3>
            <form onSubmit={handleCreateUser}>
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Kullanıcı Adı"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                />
                <input
                  type="password"
                  placeholder="Şifre (min 6 karakter)"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  minLength="6"
                />
                <input
                  type="email"
                  placeholder="E-posta (opsiyonel)"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="admin">Admin</option>
                  <option value="root">Root</option>
                </select>
                <select
                  value={newUser.gender}
                  onChange={(e) => setNewUser({ ...newUser, gender: e.target.value })}
                >
                  <option value="">Cinsiyet Seç</option>
                  <option value="male">Erkek</option>
                  <option value="female">Kadın</option>
                </select>
                <button type="submit" disabled={loading}>
                  {loading ? 'Ekleniyor...' : 'Ekle'}
                </button>
              </div>
            </form>
          </div>

          <div className="users-list">
            <h3>Mevcut Kullanıcılar</h3>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Kullanıcı Adı</th>
                  <th>E-posta</th>
                  <th>Rol</th>
                  <th>Cinsiyet</th>
                  <th>Durum</th>
                  <th>Son Giriş</th>
                  <th>Oluşturulma</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.username}</td>
                    <td>{u.email || '-'}</td>
                    <td>
                      <span className={`role-badge ${u.role}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <select
                        value={u.gender || ''}
                        onChange={(e) => handleUpdateUser(u.id, { gender: e.target.value || null })}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '13px',
                          background: u.gender === 'male' ? '#e3f2fd' : u.gender === 'female' ? '#fce4ec' : '#f5f5f5',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">-</option>
                        <option value="male">Erkek</option>
                        <option value="female">Kadın</option>
                      </select>
                    </td>
                    <td>
                      <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>{u.last_login ? formatDate(u.last_login) : 'Hiç giriş yapmadı'}</td>
                    <td>{u.created_at ? formatDate(u.created_at) : '-'}</td>
                    <td>
                      {u.id !== 1 && (
                        <div className="action-buttons">
                          <button
                            onClick={() => toggleUserStatus(u.id, u.is_active)}
                            className="btn-toggle"
                            disabled={loading}
                            title={u.is_active ? 'Devre Dışı Bırak' : 'Aktif Et'}
                          >
                            {u.is_active ? 'Kilitle' : 'Aç'}
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="btn-delete"
                            disabled={loading}
                            title="Kullanıcıyı Sil"
                          >
                            Sil
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Telegram Tab */}
      {activeTab === 'telegram' && (
        <div className="telegram-management">
          <h3>Telegram Bağlantı Yönetimi</h3>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Kullanıcıların OTP doğrulaması için bağlı Telegram hesaplarını buradan yönetebilirsiniz.
          </p>

          <div className="users-list">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Kullanıcı Adı</th>
                  <th>Telegram Hesabı</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.id}</td>
                    <td>{u.username}</td>
                    <td>
                      {u.telegram_username ? (
                        <span style={{ color: '#0088cc' }}>@{u.telegram_username}</span>
                      ) : (
                        <span style={{ color: '#999' }}>Bağlı değil</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${u.telegram_verified ? 'active' : 'inactive'}`}>
                        {u.telegram_verified ? 'Doğrulandı' : 'Doğrulanmadı'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => openTelegramModal(u)}
                        className="btn-toggle"
                        style={{ marginRight: '5px' }}
                      >
                        Yönet
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity Logs Tab */}
      {activeTab === 'activity' && (
        <div className="activity-logs">
          <h3>Sistem Aktivite Logları</h3>
          <div className="log-filters">
            <select
              value={filter.user_id}
              onChange={(e) => setFilter({ ...filter, user_id: e.target.value, page: 1 })}
            >
              <option value="">Tüm Kullanıcılar</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>
            <select
              value={filter.entity_type}
              onChange={(e) => setFilter({ ...filter, entity_type: e.target.value, page: 1 })}
            >
              <option value="">Tüm Tipler</option>
              <option value="CHANNEL">Kanal</option>
              <option value="CONTENT">İçerik</option>
              <option value="POST">Gönderi</option>
              <option value="AUTOMATION">Otomasyon</option>
              <option value="USER">Kullanıcı</option>
              <option value="TAG">Etiket</option>
            </select>
            <select
              value={filter.action}
              onChange={(e) => setFilter({ ...filter, action: e.target.value, page: 1 })}
            >
              <option value="">Tüm İşlemler</option>
              <option value="CREATE">Oluşturma</option>
              <option value="UPDATE">Güncelleme</option>
              <option value="DELETE">Silme</option>
              <option value="VIEW">Görüntüleme</option>
              <option value="PUBLISH">Yayınlama</option>
              <option value="SCHEDULE">Zamanlama</option>
              <option value="ENABLE">Etkinleştirme</option>
              <option value="DISABLE">Devre Dışı</option>
            </select>
            <input
              type="datetime-local"
              value={filter.date_from}
              onChange={(e) => setFilter({ ...filter, date_from: e.target.value, page: 1 })}
              placeholder="Başlangıç"
            />
            <input
              type="datetime-local"
              value={filter.date_to}
              onChange={(e) => setFilter({ ...filter, date_to: e.target.value, page: 1 })}
              placeholder="Bitiş"
            />
            <select
              value={filter.limit}
              onChange={(e) => setFilter({ ...filter, limit: e.target.value, page: 1 })}
            >
              <option value="50">50 Kayıt</option>
              <option value="100">100 Kayıt</option>
              <option value="200">200 Kayıt</option>
              <option value="500">500 Kayıt</option>
            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!filter.hideSystemLogs}
                onChange={(e) => setFilter({ ...filter, hideSystemLogs: !e.target.checked })}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px' }}>Sistem Loglarını Göster</span>
            </label>

            <button onClick={fetchActivityLogs} disabled={loading}>
              {loading ? 'Yükleniyor...' : 'Filtrele'}
            </button>
          </div>

          <div className="logs-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tarih/Saat</th>
                  <th>Kullanıcı</th>
                  <th>İşlem</th>
                  <th>Varlık Tipi</th>
                  <th>Varlık</th>
                  <th>Açıklama</th>
                  <th>IP Adresi</th>
                  <th>Detay</th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.map(log => (
                  <tr key={log.id} className={`log-row log-${log.action?.toLowerCase()}`}>
                    <td>{log.id}</td>
                    <td>{formatDate(log.created_at)}</td>
                    <td>
                      <span className="username-badge">
                        {log.username || 'Sistem'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`action-badge action-${log.action?.toLowerCase()}`}
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        [{getActionIcon(log.action)}] {log.action}
                      </span>
                    </td>
                    <td>{getEntityTypeLabel(log.entity_type)}</td>
                    <td>{log.entity_name || `#${log.entity_id}`}</td>
                    <td>{log.description || log.change_summary || '-'}</td>
                    <td>
                      <span className="ip-badge" title={log.user_agent}>
                        {log.ip_address || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {(log.old_data || log.new_data || log.metadata) && (
                        <button
                          onClick={() => showLogDetail(log)}
                          className="btn-detail"
                          title="Detayları Göster"
                        >
                          Detay
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {activityLogs.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                      Log kaydı bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <div className={`pagination-container ${loading ? 'loading' : ''}`}>
                <div className="pagination-info">
                  <span className="total-records">
                    Toplam <strong>{pagination.total}</strong> kayıt
                  </span>
                  <span className="page-status">
                    Sayfa <strong>{filter.page}</strong> / <strong>{pagination.totalPages}</strong>
                  </span>
                </div>

                <div className="pagination-controls">
                  <button
                    className="pagination-btn first"
                    onClick={() => {
                      setFilter({ ...filter, page: 1 });
                      fetchActivityLogs();
                    }}
                    disabled={filter.page === 1 || loading}
                    title="İlk Sayfa"
                  >
                    İlk
                  </button>

                  <button
                    className="pagination-btn prev"
                    onClick={() => {
                      setFilter({ ...filter, page: filter.page - 1 });
                      fetchActivityLogs();
                    }}
                    disabled={filter.page === 1 || loading}
                    title="Önceki Sayfa"
                  >
                    Önceki
                  </button>

                  <div className="page-numbers">
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (filter.page <= 3) {
                        pageNum = i + 1;
                      } else if (filter.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = filter.page - 2 + i;
                      }

                      return pageNum > 0 && pageNum <= pagination.totalPages ? (
                        <button
                          key={pageNum}
                          className={`page-number ${pageNum === filter.page ? 'active' : ''}`}
                          onClick={() => {
                            setFilter({ ...filter, page: pageNum });
                            fetchActivityLogs();
                          }}
                          disabled={loading}
                        >
                          {pageNum}
                        </button>
                      ) : null;
                    })}
                  </div>

                  <button
                    className="pagination-btn next"
                    onClick={() => {
                      setFilter({ ...filter, page: filter.page + 1 });
                      fetchActivityLogs();
                    }}
                    disabled={filter.page === pagination.totalPages || loading}
                    title="Sonraki Sayfa"
                  >
                    Sonraki
                  </button>

                  <button
                    className="pagination-btn last"
                    onClick={() => {
                      setFilter({ ...filter, page: pagination.totalPages });
                      fetchActivityLogs();
                    }}
                    disabled={filter.page === pagination.totalPages || loading}
                    title="Son Sayfa"
                  >
                    Son
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Login Logs Tab */}
      {activeTab === 'login' && (
        <div className="login-logs">
          <h3>Giriş/Çıkış Logları ({loginLogs.length})</h3>

          <div style={{
            background: '#f0f8ff',
            border: '1px solid #4169e1',
            padding: '10px',
            marginBottom: '15px',
            borderRadius: '5px',
            fontSize: '12px'
          }}>
            <strong>Özet:</strong>
            {' '}Toplam {loginLogs.length} kayıt
            {' '}({loginLogs.filter(l => l.action === 'LOGIN').length} Giriş,
            {' '}{loginLogs.filter(l => l.action === 'LOGOUT').length} Çıkış)
          </div>

          <div className="logs-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tarih/Saat</th>
                  <th>Kullanıcı Adı</th>
                  <th>İşlem</th>
                  <th>Durum</th>
                  <th>IP Adresi</th>
                  <th>Konum</th>
                  <th>Cihaz</th>
                  <th>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                {loginLogs.map((log) => (
                  <tr key={log.id} className={log.success ? 'log-success' : 'log-failed'}>
                    <td>{log.id}</td>
                    <td>{formatDate(log.created_at)}</td>
                    <td>{log.username}</td>
                    <td>
                      <span className="action-badge">
                        {log.action === 'LOGIN' ? 'Giriş' :
                          log.action === 'LOGOUT' ? 'Çıkış' :
                            log.action === 'LOGIN_ATTEMPT' ? 'Giriş Denemesi' :
                              log.action || 'Bilinmeyen'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${log.success ? 'success' : 'failed'}`}>
                        {log.success === 1 || log.success === true || log.success === '1' ? 'Başarılı' : 'Başarısız'}
                      </span>
                    </td>
                    <td>{log.ip_address}</td>
                    <td>{log.location || '-'}</td>
                    <td title={log.user_agent}>
                      {log.browser || (log.user_agent ? log.user_agent.substring(0, 30) + '...' : '-')}
                    </td>
                    <td>{log.reason || '-'}</td>
                  </tr>
                ))}
                {loginLogs.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                      Giriş logu bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '15px' }}>
            <button
              onClick={fetchLoginLogs}
              disabled={loading}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Yükleniyor...' : 'Yenile'}
            </button>
          </div>
        </div>
      )}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && (
        <div className="maintenance-section">
          <h3>Bakım İşlemleri</h3>

          <div className="maintenance-cards">
            <div className="maintenance-card">
              <h4>Log Temizleme</h4>
              <p>Eski logları temizleyerek veritabanı performansını artırın</p>
              <div className="maintenance-actions">
                <button
                  onClick={() => cleanOldLogs(7)}
                  className="btn-warning"
                >
                  7 Günden Eski Logları Temizle
                </button>
                <button
                  onClick={() => cleanOldLogs(30)}
                  className="btn-warning"
                >
                  30 Günden Eski Logları Temizle
                </button>
                <button
                  onClick={() => cleanOldLogs(90)}
                  className="btn-warning"
                >
                  90 Günden Eski Logları Temizle
                </button>
              </div>
            </div>

            <div className="maintenance-card">
              <h4>Log İstatistikleri</h4>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Görüntülenen Aktivite Logu:</span>
                  <span className="stat-value">{activityLogs.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Toplam Giriş Logu:</span>
                  <span className="stat-value">{loginLogs.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Aktif Kullanıcı:</span>
                  <span className="stat-value">{users.filter(u => u.is_active).length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Sistem Logları:</span>
                  <span className="stat-value">{filter.hideSystemLogs ? 'Gizli' : 'Görünür'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content log-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Log Detayları</h3>
              <button
                className="modal-close"
                onClick={() => setShowDetailModal(false)}
              >
                X
              </button>
            </div>

            <div className="log-detail">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Log ID:</label>
                  <span>#{selectedLog.id}</span>
                </div>
                <div className="detail-item">
                  <label>Tarih:</label>
                  <span>{formatDate(selectedLog.created_at)}</span>
                </div>
                <div className="detail-item">
                  <label>Kullanıcı:</label>
                  <span>{selectedLog.username || 'Sistem'}</span>
                </div>
                <div className="detail-item">
                  <label>İşlem:</label>
                  <span className={`action-badge action-${selectedLog.action?.toLowerCase()}`}>
                    [{getActionIcon(selectedLog.action)}] {selectedLog.action}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Varlık Tipi:</label>
                  <span>{getEntityTypeLabel(selectedLog.entity_type)}</span>
                </div>
                <div className="detail-item">
                  <label>Varlık:</label>
                  <span>{selectedLog.entity_name || `ID: ${selectedLog.entity_id}`}</span>
                </div>
                <div className="detail-item">
                  <label>IP Adresi:</label>
                  <span>{selectedLog.ip_address || 'N/A'}</span>
                </div>
                <div className="detail-item full-width">
                  <label>User Agent:</label>
                  <span>{selectedLog.user_agent || 'N/A'}</span>
                </div>
              </div>

              {selectedLog.description && (
                <div className="detail-section">
                  <h4>Açıklama</h4>
                  <p>{selectedLog.description}</p>
                </div>
              )}

              {selectedLog.metadata && (
                <div className="detail-section">
                  <h4>Metadata</h4>
                  <pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                </div>
              )}

              {selectedLog.changes && selectedLog.changes.length > 0 && (
                <div className="detail-section">
                  <h4>Değişiklikler</h4>
                  <table className="changes-table">
                    <thead>
                      <tr>
                        <th>Alan</th>
                        <th>Eski Değer</th>
                        <th>Yeni Değer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLog.changes.map((change, idx) => (
                        <tr key={idx}>
                          <td>{change.field}</td>
                          <td className="old-value">
                            <code>{JSON.stringify(change.old)}</code>
                          </td>
                          <td className="new-value">
                            <code>{JSON.stringify(change.new)}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedLog.old_data && (
                <div className="detail-section">
                  <h4>Eski Veri</h4>
                  <pre className="data-preview">{JSON.stringify(selectedLog.old_data, null, 2)}</pre>
                </div>
              )}

              {selectedLog.new_data && (
                <div className="detail-section">
                  <h4>Yeni Veri</h4>
                  <pre className="data-preview">{JSON.stringify(selectedLog.new_data, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Telegram Management Modal */}
      {showTelegramModal && selectedUserForTelegram && (
        <div className="modal-overlay" onClick={() => setShowTelegramModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Telegram Bağlantısı - {selectedUserForTelegram.username}</h3>
              <button
                className="modal-close"
                onClick={() => setShowTelegramModal(false)}
              >
                X
              </button>
            </div>

            <div className="modal-body" style={{ padding: '20px' }}>
              {telegramInfo ? (
                <div>
                  <div style={{
                    background: telegramInfo.connected ? '#e8f5e9' : '#fff3e0',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>
                      {telegramInfo.connected ? 'Bağlı' : 'Bağlı Değil'}
                    </h4>

                    {telegramInfo.connected && (
                      <div style={{ fontSize: '14px' }}>
                        <p><strong>Telegram:</strong> @{telegramInfo.username}</p>
                        <p><strong>Chat ID:</strong> {telegramInfo.chat_id}</p>
                        <p><strong>Son Güncelleme:</strong> {formatDate(telegramInfo.updated_at)}</p>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {telegramInfo.connected && (
                      <button
                        onClick={removeTelegramConnection}
                        style={{
                          padding: '10px 20px',
                          background: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Bağlantıyı Kaldır
                      </button>
                    )}

                    <button
                      onClick={generateTelegramCode}
                      style={{
                        padding: '10px 20px',
                        background: '#0088cc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {telegramInfo.connected ? 'Yeni Hesap Bağla' : 'Bağlantı Kodu Oluştur'}
                    </button>
                  </div>

                  {telegramCode && (
                    <div style={{
                      marginTop: '20px',
                      padding: '15px',
                      background: '#e3f2fd',
                      borderRadius: '8px',
                      border: '1px solid #2196f3'
                    }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Bağlantı Kodu</h4>

                      <div style={{
                        background: '#fff',
                        padding: '15px',
                        borderRadius: '6px',
                        textAlign: 'center',
                        marginBottom: '15px'
                      }}>
                        <code style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          letterSpacing: '3px',
                          color: '#1976d2'
                        }}>
                          {telegramCode.code}
                        </code>
                      </div>

                      <div style={{ fontSize: '13px', color: '#555' }}>
                        {telegramCode.instructions?.map((instruction, idx) => (
                          <p key={idx} style={{ margin: '5px 0' }}>{instruction}</p>
                        ))}
                      </div>

                      <p style={{
                        fontSize: '12px',
                        color: '#f57c00',
                        marginTop: '10px'
                      }}>
                        Kod geçerlilik: {formatDate(telegramCode.expiresAt)}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Yükleniyor...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;